import type { Olympus } from '@owner/olympus-client'

// Hermes is normally the iframe *host* (it embeds Hestia). This module is the
// other direction: when another Owner app embeds Hermes, Hermes runs as the
// guest and bootstraps a session from a token handed over by the parent. The
// access token is sent as a bearer (client 'mobile') rather than Hermes' usual
// httpOnly cookie, because the parent and guest are different origins and the
// cookie can't be shared.

const IFRAME_SESSION_STORAGE_KEY = 'owner-iframe-session'

// Parent origins allowed to hand Hermes a session. Anything else is ignored on
// the postMessage channel — this is the trust boundary for the handoff.
export const SAFE_PARENT_ORIGINS = [
  'https://internal.owner.com', // Hercules production
  'https://dev-internal.owner.com', // Hercules staging
  'http://localhost:8091', // Hercules web dev
  'https://app.owner.com', // Hestia production
  'https://dev-app.owner.com', // Hestia staging
  'http://localhost:8086', // Hestia web dev
  'http://localhost:8087', // Alternate Hestia web dev
]

// Hercules preview deploys live on per-branch hashed subdomains of its
// dedicated hercules-preview.owner.com namespace, so they can't be
// enumerated above.
const HERCULES_PREVIEW_ORIGIN =
  /^https:\/\/[a-z0-9]+\.hercules-preview\.owner\.com$/

export interface IframeAuthSession {
  // Bearer token minted for the parent app; replayed on every Olympus request.
  accessToken: string
  // The parent app's audience (e.g. 'hestia' for Hercules); must match what the
  // token was minted for, so the backend authorizes it.
  audience: string
  // Olympus base the token was minted against, for context/telemetry. Not used
  // to switch the client base: the embedded client keeps Hermes' own
  // VITE_OLYMPUS_URL, and every env's API hosts (e.g. api.owner.com /
  // api.ordersave.com) are CNAMEs to the same Olympus, so it always matches.
  apiUrl: string
  // The validated parent window origin, kept for replying over postMessage.
  parentOrigin: string
  // Epoch ms when the parent-minted token expires. A stored session past this is
  // treated as absent, so a stale token can't be replayed indefinitely.
  expiresAt: number
}

// The full payload the parent posts during the handoff: the persisted session
// fields plus the one-shot data Hermes consumes while bootstrapping (the brand
// to land on, the session expiry, and the user record). `expiresAt` is an ISO
// string here but epoch ms on the stored session, hence the Omit.
export interface IframeSessionHydrationPayload extends Omit<
  IframeAuthSession,
  'expiresAt'
> {
  brandId: string
  locationId?: string
  expiresAt: string
  user: Olympus.Users.User
}

export interface IframeSessionHydrationMessage {
  type: 'HERMES_SESSION_HYDRATION'
  session: IframeSessionHydrationPayload
}

export interface IframeNavigationMessage {
  type: 'HERMES_EMBED_NAVIGATE'
  target: 'reports'
}

export const IFRAME_READY_MESSAGE = { type: 'HERMES_IFRAME_READY' } as const
export const IFRAME_HYDRATION_SUCCESS_TYPE = 'HERMES_SESSION_HYDRATION_SUCCESS'
export const IFRAME_HYDRATION_ERROR_TYPE = 'HERMES_SESSION_HYDRATION_ERROR'

export const isSafeParentOrigin = (origin: string): boolean =>
  SAFE_PARENT_ORIGINS.includes(origin) || HERCULES_PREVIEW_ORIGIN.test(origin)

export const isInsideIframe = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    return window.self !== window.top
  } catch {
    // A SecurityError from a cross-origin parent means we are framed.
    return true
  }
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

export const isIframeSessionHydrationMessage = (
  data: unknown
): data is IframeSessionHydrationMessage => {
  if (typeof data !== 'object' || data === null) return false
  const message = data as Record<string, unknown>
  if (message.type !== 'HERMES_SESSION_HYDRATION') return false

  const session = message.session
  if (typeof session !== 'object' || session === null) return false
  const candidate = session as Record<string, unknown>

  return (
    isNonEmptyString(candidate.accessToken) &&
    isNonEmptyString(candidate.audience) &&
    isNonEmptyString(candidate.apiUrl) &&
    isNonEmptyString(candidate.brandId) &&
    (candidate.locationId === undefined ||
      isNonEmptyString(candidate.locationId)) &&
    isNonEmptyString(candidate.expiresAt) &&
    typeof candidate.user === 'object' &&
    candidate.user !== null
  )
}

export const isIframeNavigationMessage = (
  data: unknown
): data is IframeNavigationMessage => {
  if (typeof data !== 'object' || data === null) return false
  const message = data as Record<string, unknown>
  return (
    message.type === 'HERMES_EMBED_NAVIGATE' && message.target === 'reports'
  )
}

export const getIframeSession = (): IframeAuthSession | null => {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(IFRAME_SESSION_STORAGE_KEY)
  if (!raw) return null
  // Any unusable stored value — malformed, legacy (pre-expiry), expired, or
  // unparseable — is evicted rather than skipped, so it can't be read later.
  try {
    const parsed = JSON.parse(raw) as Partial<IframeAuthSession>
    if (
      !isNonEmptyString(parsed.accessToken) ||
      !isNonEmptyString(parsed.audience) ||
      !isNonEmptyString(parsed.apiUrl) ||
      !isNonEmptyString(parsed.parentOrigin) ||
      typeof parsed.expiresAt !== 'number' ||
      parsed.expiresAt <= Date.now()
    ) {
      clearIframeSession()
      return null
    }
    return parsed as IframeAuthSession
  } catch {
    clearIframeSession()
    return null
  }
}

/**
 * The stored iframe session, but only when Hermes is actually running framed. A
 * leftover owner-iframe-session in localStorage must not attach the parent's
 * bearer token to a normal top-level session.
 */
export const getActiveIframeSession = (): IframeAuthSession | null =>
  isInsideIframe() ? getIframeSession() : null

export const setIframeSession = (session: IframeAuthSession): void => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(
    IFRAME_SESSION_STORAGE_KEY,
    JSON.stringify(session)
  )
}

export const clearIframeSession = (): void => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(IFRAME_SESSION_STORAGE_KEY)
}
